import type {
  Field,
  PricingTable,
  PricingTableSection,
  PricingTableSummaryValues,
  PricingTableSummaryValue,
} from '../../types';
import { PricingTableHelper } from '../pricing-table.helper';

const DEFAULT_SUMMARY_VALUE: PricingTableSummaryValue = {
  value: '',
  enabled: false,
  flatFee: false,
  displayName: '',
};

const DEFAULT_SUMMARY_VALUES: Required<PricingTableSummaryValues> = {
  displayName: '',
  discount: { ...DEFAULT_SUMMARY_VALUE },
  tax: { ...DEFAULT_SUMMARY_VALUE },
  price: { ...DEFAULT_SUMMARY_VALUE },
  enableTaxGrouping: null,
  extendedSummaries: [],
};

const ensureSummaryValue = (value?: PricingTableSummaryValue): PricingTableSummaryValue => {
  if (!value) {
    return { ...DEFAULT_SUMMARY_VALUE };
  }

  return {
    value: value.value ?? DEFAULT_SUMMARY_VALUE.value,
    enabled: value.enabled ?? DEFAULT_SUMMARY_VALUE.enabled,
    flatFee: value.flatFee ?? DEFAULT_SUMMARY_VALUE.flatFee,
    displayName: value.displayName ?? DEFAULT_SUMMARY_VALUE.displayName,
  };
};

const ensureSummaryValues = (
  values?: PricingTableSummaryValues
): Required<PricingTableSummaryValues> => {
  if (!values) {
    return { ...DEFAULT_SUMMARY_VALUES };
  }

  return {
    displayName: values.displayName ?? DEFAULT_SUMMARY_VALUES.displayName,
    discount: ensureSummaryValue(values.discount),
    tax: ensureSummaryValue(values.tax),
    price: ensureSummaryValue(values.price),
    extendedSummaries: values.extendedSummaries ?? DEFAULT_SUMMARY_VALUES.extendedSummaries,
    enableTaxGrouping: values.enableTaxGrouping ?? DEFAULT_SUMMARY_VALUES.enableTaxGrouping,
  };
};

export const getSectionsWithRowsFromInputValues = (
  node: PricingTable,
  recipientInputFields?: Field[]
): PricingTableSection[] => {
  const nodeFields = node.fields || [];
  const updatedFields = recipientInputFields ? recipientInputFields : nodeFields;
  const sectionsWithEnabledRows = PricingTableHelper.sectionsWithEnabledRows(
    node.pricingTableSections,
    updatedFields
  );
  return PricingTableHelper.sectionsWithRowsFromInputValues(sectionsWithEnabledRows, updatedFields);
};

export const getSummaryValues = (
  node?: PricingTable,
  fields?: Field[]
): Required<PricingTableSummaryValues> => {
  if (!node) {
    return { ...DEFAULT_SUMMARY_VALUES };
  }

  if (node.preCalculated) {
    return ensureSummaryValues(node.pricingTableSummaryValues);
  }

  const calculatedValues = PricingTableHelper.calculateSummaryValues(
    getSectionsWithRowsFromInputValues(node, fields),
    node.pricingTableSummaryValues ?? DEFAULT_SUMMARY_VALUES
  );

  return ensureSummaryValues(calculatedValues);
};

export const getSummaryValuesPriceEnabled = (node?: PricingTable): boolean => {
  if (!node) return false;
  const summaryValues = getSummaryValues(node);
  return summaryValues.price.enabled;
};
